/** Géométrie sans dépendance serveur (utilisable dans les composants client). */

/** Distance à vol d'oiseau en km. */
export function haversineKmPure(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371, toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * Précision des coordonnées (Clinic.geoPrecision) :
 *  - "precise" : coordonnées de la source ou géocodage du lieu / du numéro ;
 *  - "street"  : géocodage au niveau de la rue ;
 *  - "quartier" / "ville" : centroïde, JAMAIS affiché comme un point.
 * Seules "precise" et "street" vont sur une carte, dans « autour de moi », le
 * tri par distance et l'itinéraire. Le JSON-LD `geo` exige "precise".
 */
export const MAPPABLE_PRECISIONS = ["precise", "street"] as const;
export function isMappable(geoPrecision: string | null | undefined): boolean {
  return (MAPPABLE_PRECISIONS as readonly string[]).includes(geoPrecision ?? "");
}

/** « Autour de moi » : rayon affiché, et marge ajoutée côté serveur pour compenser l'arrondi de la position. */
export const NEAR_ME_RADIUS_KM = 25;
export const NEAR_ME_ROUNDING_MARGIN_KM = 1.5;
export const NEAR_ME_MAX_RESULTS = 20;

/**
 * Arrondi à 2 décimales (≈ 1,1 km en latitude, ≈ 0,9 km en longitude au Maroc) :
 * la position exacte ne quitte jamais le navigateur. Erreur maximale ≈ 0,75 km,
 * couverte par NEAR_ME_ROUNDING_MARGIN_KM.
 */
export function roundForPrivacy(v: number): number {
  return Math.round(v * 100) / 100;
}

/**
 * Distance exacte depuis `from`, filtre au rayon et tri du plus proche au plus
 * loin (calculé dans le navigateur à partir de la position exacte). Les
 * éléments sans coordonnées sont écartés.
 */
export function nearestWithin<T extends { lat: number | null; lng: number | null }>(
  items: T[],
  from: { lat: number; lng: number },
  radiusKm: number,
  limit = Infinity,
): Array<T & { distanceKm: number }> {
  const out: Array<T & { distanceKm: number }> = [];
  for (const it of items) {
    if (it.lat == null || it.lng == null) continue;
    const distanceKm = haversineKmPure(from.lat, from.lng, it.lat, it.lng);
    if (distanceKm <= radiusKm) out.push({ ...it, distanceKm });
  }
  return out.sort((a, b) => a.distanceKm - b.distanceKm).slice(0, limit);
}
